// src/lib/schema/components/BreadcrumbSchema.tsx

import React from 'react';
import { generateBreadcrumbSchema, generateEcommerceBreadcrumbSchema, schemaToString } from '@/lib/schema';

/**
 * Interface for breadcrumb item
 */
interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Props for the BreadcrumbSchema component
 */
interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

/**
 * Component that renders the JSON-LD schema for breadcrumbs
 */
export function BreadcrumbSchema({ items }: BreadcrumbSchemaProps): React.ReactElement | null {
  // Return null if required props are missing
  if (!items || !Array.isArray(items) || items.length === 0) return null;

  // Generate the breadcrumb schema
  const breadcrumbSchema = generateBreadcrumbSchema(items);

  // Return null if schema generation failed
  if (!breadcrumbSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(breadcrumbSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}

/**
 * Props for the EcommerceBreadcrumbSchema component
 */
interface EcommerceBreadcrumbSchemaProps {
  baseURL: string;
  lang: string;
  currentPageName: string;
  currentPageURL: string;
  category?: string;
  categoryURL?: string;
  subcategory?: string;
  subcategoryURL?: string;
}

/**
 * Component that renders the JSON-LD schema for e-commerce breadcrumbs
 */
export function EcommerceBreadcrumbSchema({
  baseURL,
  lang,
  currentPageName,
  currentPageURL,
  category,
  categoryURL,
  subcategory,
  subcategoryURL
}: EcommerceBreadcrumbSchemaProps): React.ReactElement | null {
  // Return null if required props are missing
  if (!baseURL || !lang || !currentPageName || !currentPageURL) return null;

  // Prepare options for e-commerce breadcrumb
  const options = {
    baseURL,
    lang,
    currentPageName,
    currentPageURL,
    category,
    categoryURL,
    subcategory,
    subcategoryURL
  };

  // Generate the e-commerce breadcrumb schema
  const breadcrumbSchema = generateEcommerceBreadcrumbSchema(options);

  // Return null if schema generation failed
  if (!breadcrumbSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(breadcrumbSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}